import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { MessageDetails, MessageThread } from '@/components/messages'
import type { MessageNodeConfig } from '@/components/nodes'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import type { Message } from '@/types'
import { MessageStatus } from '@/types/enums'
import { messageService } from '@/services'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { LoadingState } from '@/components/layout'

export default function MessageDetailPage() {
  const { messageId } = useParams<{ messageId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [loading, setLoading] = useState(false)

  // Calculate the number of replies (excluding drafts)
  const replyCount =
    selectedMessage?.messages?.filter((reply) => reply.status !== MessageStatus.Draft).length || 0

  // Get active tab from URL or default to 'unread'
  const activeTab = searchParams.get('tab') || 'unread'

  // Load message when messageId changes in URL
  useEffect(() => {
    const loadMessageById = async (id: string) => {
      setLoading(true)
      try {
        const message = await messageService.getById(id)
        setSelectedMessage(message)

        // Mark message as read when viewed
        if (user && message && !message.readBy.some((read) => read.userId === user.id)) {
          try {
            const updatedMessage = await messageService.markAsRead(message.id, user.id)
            setSelectedMessage(updatedMessage)
          } catch (error) {
            console.error('Failed to mark message as read:', error)
          }
        }
      } catch (error) {
        console.error('Failed to load message:', error)
        toast.error('Failed to load message')
        navigate('/messages', { replace: true })
      } finally {
        setLoading(false)
      }
    }

    if (messageId) {
      loadMessageById(messageId)
    } else {
      navigate('/messages', { replace: true })
    }
  }, [messageId, user, navigate])

  const handleReply = () => {
    // The reply functionality is handled within MessageThread
    // This could be extended to open a separate reply dialog if needed
  }

  const handleEdit = () => {
    if (selectedMessage) {
      navigate(`/messages/${selectedMessage.id}/edit?tab=${activeTab}`)
    }
  }

  const handleDelete = () => {
    navigate(`/messages?tab=${activeTab}`)
  }

  const handleParentClick = (parentMessage: Message) => {
    // Navigate to the parent message, preserving the tab
    navigate(`/messages/${parentMessage.id}?tab=${activeTab}`)
  }

  const handleReplyCreated = (updatedParent: Message) => {
    // Update the selected message with the updated parent that includes the new reply
    setSelectedMessage(updatedParent)
  }

  // Configuration for MessageThread display
  const messageConfig: MessageNodeConfig = {
    showTitle: false,
    showDescription: true,
    showReplyCount: true,
    showMessageType: true,
    showReadStatus: true,
    showCreator: true,
    showDate: true,
    showRootReplyForm: true,
    showWorkspace: false,
    showProjects: false,
    showTasks: false,
    showDocuments: false,
    showRelationshipCounts: false,
  }

  // Show loading state when fetching message
  if (loading) {
    return <LoadingState />
  }

  if (!selectedMessage) {
    return null
  }

  return (
    <div className="space-y-6">
      <MessageDetails
        message={selectedMessage}
        onReply={handleReply}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onParentClick={handleParentClick}
      />

      {/* Replies Section */}
      <Card>
        <CardHeader>
          <CardTitle>Replies</CardTitle>
          <CardDescription>
            {replyCount === 0
              ? 'No replies to this message yet'
              : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'} to this message`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MessageThread
            message={selectedMessage}
            onReplyCreated={handleReplyCreated}
            showTitle={false}
            showParentMessage={false}
            messageConfig={messageConfig}
          />
        </CardContent>
      </Card>
    </div>
  )
}
